/**
 * passkey.ts - Passkey (WebAuthn) authentication handling
 */

import { startRegistration, startAuthentication, browserSupportsWebAuthn } from '@simplewebauthn/browser';
import { setCurrentUser } from './auth-state';
import { updateAuthUI } from './auth-ui';
import { closeAuthModal } from './auth-modals';

const API_BASE = '/api/auth/passkey';

interface PasskeyInfo {
  id: string;
  name: string;
  createdAt: string;
  lastUsedAt: string | null;
  deviceType: string | null;
  backedUp: boolean;
}

/**
 * Check if WebAuthn is supported in this browser
 */
export function isPasskeySupported(): boolean {
  return browserSupportsWebAuthn();
}

/**
 * Check if passkeys are available for this user/device
 */
export async function checkPasskeyAvailability(): Promise<{ available: boolean; hasPasskeys: boolean }> {
  try {
    const response = await fetch(`${API_BASE}/available`);
    if (!response.ok) {
      return { available: false, hasPasskeys: false };
    }
    return await response.json();
  } catch (error) {
    console.error('Error checking passkey availability:', error);
    return { available: false, hasPasskeys: false };
  }
}

/**
 * Register a new passkey for the current user
 * Requires user to be logged in
 */
export async function registerPasskey(name?: string): Promise<{ success: boolean; error?: string; passkey?: PasskeyInfo }> {
  try {
    // Get registration options from server
    const optionsResponse = await fetch(`${API_BASE}/register/options`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });

    if (!optionsResponse.ok) {
      const error = await optionsResponse.json();
      return { success: false, error: error.error || 'Failed to get registration options' };
    }

    const { options, challengeId } = await optionsResponse.json();

    // Start the WebAuthn registration ceremony
    let credential;
    try {
      credential = await startRegistration({ optionsJSON: options });
    } catch (error: any) {
      if (error.name === 'NotAllowedError') {
        return { success: false, error: 'Registration was cancelled or timed out' };
      }
      throw error;
    }

    // Verify registration with server
    const verifyResponse = await fetch(`${API_BASE}/register/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        challengeId,
        response: credential,
        name: name || getDeviceName(),
      }),
    });

    if (!verifyResponse.ok) {
      const error = await verifyResponse.json();
      return { success: false, error: error.error || 'Failed to verify passkey registration' };
    }

    const result = await verifyResponse.json();
    return { success: true, passkey: result.passkey };
  } catch (error: any) {
    console.error('Passkey registration error:', error);
    return { success: false, error: error.message || 'Failed to register passkey' };
  }
}

/**
 * Sign up with a passkey (no password required)
 * Creates a new account using only passkey authentication
 */
export async function signupWithPasskey(
  username: string,
  email: string
): Promise<{ success: boolean; error?: string; user?: any; requiresVerification?: boolean; message?: string }> {
  try {
    // Get signup options from server
    const optionsResponse = await fetch(`${API_BASE}/signup/options`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, email }),
    });

    if (!optionsResponse.ok) {
      const error = await optionsResponse.json();
      return { success: false, error: error.error || 'Failed to start signup' };
    }

    const { options, challengeId } = await optionsResponse.json();

    // Start the WebAuthn registration ceremony
    let credential;
    try {
      credential = await startRegistration({ optionsJSON: options });
    } catch (error: any) {
      if (error.name === 'NotAllowedError') {
        return { success: false, error: 'Registration was cancelled or timed out' };
      }
      throw error;
    }

    // Verify registration and create account
    const verifyResponse = await fetch(`${API_BASE}/signup/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        challengeId,
        response: credential,
        passkeyName: getDeviceName(),
      }),
    });

    if (!verifyResponse.ok) {
      const error = await verifyResponse.json();
      return { success: false, error: error.error || 'Failed to complete signup' };
    }

    const result = await verifyResponse.json();

    // Handle email verification requirement (no auto-login)
    if (result.requiresVerification) {
      return {
        success: true,
        requiresVerification: true,
        message: result.message || 'Please check your email to verify your account.'
      };
    }

    return { success: true, user: result.user };
  } catch (error: any) {
    console.error('Passkey signup error:', error);
    return { success: false, error: error.message || 'Failed to sign up with passkey' };
  }
}

/**
 * Authenticate using a passkey
 * Returns user data on success
 */
export async function authenticateWithPasskey(): Promise<{ success: boolean; error?: string; user?: any; requiresVerification?: boolean; email?: string }> {
  try {
    // Get authentication options from server
    const optionsResponse = await fetch(`${API_BASE}/authenticate/options`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!optionsResponse.ok) {
      const error = await optionsResponse.json();
      return { success: false, error: error.error || 'Failed to get authentication options' };
    }

    const { options, challengeId } = await optionsResponse.json();

    // Start the WebAuthn authentication ceremony
    let credential;
    try {
      credential = await startAuthentication({ optionsJSON: options });
    } catch (error: any) {
      if (error.name === 'NotAllowedError') {
        return { success: false, error: 'Authentication was cancelled or timed out' };
      }
      throw error;
    }

    // Verify authentication with server
    const verifyResponse = await fetch(`${API_BASE}/authenticate/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        challengeId,
        response: credential,
      }),
    });

    if (!verifyResponse.ok) {
      const error = await verifyResponse.json();
      // Check if email verification is required
      if (error.requiresVerification) {
        return {
          success: false,
          error: error.error || 'Please verify your email before logging in',
          requiresVerification: true,
          email: error.email,
        };
      }
      return { success: false, error: error.error || 'Failed to verify passkey authentication' };
    }

    const result = await verifyResponse.json();
    return { success: true, user: result.user };
  } catch (error: any) {
    console.error('Passkey authentication error:', error);
    return { success: false, error: error.message || 'Failed to authenticate with passkey' };
  }
}

/**
 * Get list of user's registered passkeys
 */
export async function listPasskeys(): Promise<PasskeyInfo[]> {
  try {
    const response = await fetch(`${API_BASE}/list`, {
      credentials: 'include',
    });

    if (!response.ok) {
      return [];
    }

    const data = await response.json();
    return data.passkeys || [];
  } catch (error) {
    console.error('Error listing passkeys:', error);
    return [];
  }
}

/**
 * Delete a passkey
 */
export async function deletePasskey(passkeyId: string): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE}/${passkeyId}`, {
      method: 'DELETE',
      credentials: 'include',
    });

    return response.ok;
  } catch (error) {
    console.error('Error deleting passkey:', error);
    return false;
  }
}

/**
 * Rename a passkey
 */
export async function renamePasskey(passkeyId: string, name: string): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE}/${passkeyId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ name }),
    });

    return response.ok;
  } catch (error) {
    console.error('Error renaming passkey:', error);
    return false;
  }
}

/**
 * Get a friendly device name based on user agent
 */
function getDeviceName(): string {
  const ua = navigator.userAgent;

  if (ua.includes('iPhone')) return 'iPhone';
  if (ua.includes('iPad')) return 'iPad';
  if (ua.includes('Mac')) return 'Mac';
  if (ua.includes('Windows')) return 'Windows PC';
  if (ua.includes('Android')) return 'Android Device';
  if (ua.includes('Linux')) return 'Linux PC';

  return 'Passkey';
}

/**
 * Handle passkey signup button click
 */
export async function handlePasskeySignup(): Promise<void> {
  const usernameInput = document.getElementById('register-username') as HTMLInputElement;
  const emailInput = document.getElementById('register-email') as HTMLInputElement;
  const errorDiv = document.getElementById('register-error');
  const passkeyBtn = document.getElementById('passkey-signup-btn') as HTMLButtonElement;

  if (!usernameInput || !emailInput) {
    if (errorDiv) errorDiv.textContent = 'Form not found';
    return;
  }

  const username = usernameInput.value.trim();
  const email = emailInput.value.trim();

  // Validate inputs
  if (!username) {
    if (errorDiv) errorDiv.textContent = window.i18n?.t('auth.usernameRequired') || 'Username is required';
    usernameInput.focus();
    return;
  }

  if (!email) {
    if (errorDiv) errorDiv.textContent = window.i18n?.t('auth.emailRequired') || 'Email is required';
    emailInput.focus();
    return;
  }

  // Email validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    if (errorDiv) errorDiv.textContent = window.i18n?.t('auth.invalidEmail') || 'Invalid email format';
    emailInput.focus();
    return;
  }

  if (passkeyBtn) {
    passkeyBtn.disabled = true;
    passkeyBtn.innerHTML = '<span class="btn-spinner"></span>';
  }

  if (errorDiv) {
    errorDiv.textContent = '';
  }

  const result = await signupWithPasskey(username, email);

  if (passkeyBtn) {
    passkeyBtn.disabled = false;
    passkeyBtn.innerHTML = `
      <svg viewBox="0 0 24 24" width="18" height="18" style="margin-right: 8px;">
        <path fill="currentColor" d="M12.65 10A5.99 5.99 0 0 0 7 6c-3.31 0-6 2.69-6 6s2.69 6 6 6a5.99 5.99 0 0 0 5.65-4H17v4h4v-4h2v-4H12.65zM7 14c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z"/>
      </svg>
      <span data-i18n="auth.registerWithPasskey">Register with Passkey</span>
    `;
  }

  if (result.success) {
    if (result.requiresVerification) {
      // Email verification required - show success message like regular registration
      console.log('[Passkey] Registration requires email verification, showing success message');
      const successDiv = document.getElementById('register-success');
      console.log('[Passkey] successDiv found:', !!successDiv);
      const formFields = document.getElementById('register-form-fields');

      // Hide all wizard steps
      const step1 = document.getElementById('register-step-1');
      const step2SSO = document.getElementById('register-step-2-sso');
      const step2Credentials = document.getElementById('register-step-2-credentials');
      const step3Password = document.getElementById('register-step-3-password');
      if (step1) step1.style.display = 'none';
      if (step2SSO) step2SSO.style.display = 'none';
      if (step2Credentials) step2Credentials.style.display = 'none';
      if (step3Password) step3Password.style.display = 'none';
      if (formFields) formFields.style.display = 'none';

      if (successDiv) {
        const registrationTitle = window.i18n ? window.i18n.t('auth.registrationSuccessful') : 'Registration Successful!';
        const spamFolderMsg = window.i18n ? window.i18n.t('auth.checkSpamFolder') : 'Check your spam folder if you don\'t see the email.';
        const closeButton = window.i18n ? window.i18n.t('common.close') : 'Close';

        successDiv.innerHTML = `
          <div style="text-align: center; padding: 20px;">
            <div style="font-size: 48px; margin-bottom: 15px;">✉️</div>
            <h3 style="margin: 0 0 10px 0; color: #155724;">${registrationTitle}</h3>
            <p style="margin: 10px 0;">${result.message}</p>
            <p style="font-size: 14px; color: #666;">${spamFolderMsg}</p>
            <button onclick="closeAuthModal()" style="margin-top: 20px; padding: 10px 20px; background-color: #28a745; color: white; border: none; border-radius: 4px; cursor: pointer;">
              ${closeButton}
            </button>
          </div>
        `;
        successDiv.style.display = 'block';
      }
    } else if (result.user) {
      // Auto-login (legacy flow)
      setCurrentUser(result.user);
      closeAuthModal();
      updateAuthUI(result.user);

      // Reload tournaments if on that page
      if (window.loadTournamentsList) {
        window.loadTournamentsList();
      }
    }
  } else if (errorDiv && result.error) {
    errorDiv.textContent = result.error;
  }
}

/**
 * Handle passkey login button click
 */
export async function handlePasskeyLogin(): Promise<void> {
  const passkeyBtn = document.getElementById('passkey-login-btn') as HTMLButtonElement;
  const errorDiv = document.getElementById('login-error');

  if (passkeyBtn) {
    passkeyBtn.disabled = true;
    passkeyBtn.innerHTML = '<span class="btn-spinner"></span>';
  }

  if (errorDiv) {
    errorDiv.textContent = '';
  }

  const result = await authenticateWithPasskey();

  if (passkeyBtn) {
    passkeyBtn.disabled = false;
    passkeyBtn.innerHTML = `
      <svg viewBox="0 0 24 24" width="18" height="18" style="margin-right: 8px;">
        <path fill="currentColor" d="M12 1C8.14 1 5 4.14 5 8c0 2.38 1.19 4.47 3 5.74V17a1 1 0 0 0 1 1h1v1a1 1 0 0 0 1 1h1v2a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1v-2h1a1 1 0 0 0 1-1v-1h1a1 1 0 0 0 1-1v-3.26c1.81-1.27 3-3.36 3-5.74 0-3.86-3.14-7-7-7zm0 2c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2z"/>
      </svg>
      <span data-i18n="auth.loginWithPasskey">Login with Passkey</span>
    `;
  }

  if (result.success && result.user) {
    setCurrentUser(result.user);
    closeAuthModal();
    updateAuthUI(result.user);

    // Reload tournaments if on that page
    if (window.loadTournamentsList) {
      window.loadTournamentsList();
    }
  } else if (errorDiv) {
    if (result.requiresVerification && result.email) {
      // Email not verified - show message with resend option
      errorDiv.innerHTML = `
        ${result.error}<br>
        <button onclick="resendVerificationFromLogin('${result.email}')" style="margin-top: 10px; padding: 5px 10px; cursor: pointer;">
          Resend Verification Email
        </button>
      `;
    } else if (result.error) {
      errorDiv.textContent = result.error;
    }
  }
}

/**
 * Initialize passkey buttons in login and register modals
 */
export async function initializePasskeyButton(): Promise<void> {
  if (!isPasskeySupported()) {
    return;
  }

  // Show passkey login button if WebAuthn is supported
  const passkeyLoginBtn = document.getElementById('passkey-login-btn');
  const passkeyDivider = document.getElementById('passkey-divider-login');

  if (passkeyLoginBtn) {
    passkeyLoginBtn.style.display = 'flex';
  }
  if (passkeyDivider) {
    passkeyDivider.style.display = 'flex';
  }

  // Show passkey option in step 2b of registration wizard
  const passkeyOption = document.getElementById('register-passkey-option');
  if (passkeyOption) {
    passkeyOption.style.display = 'block';
  }
}

/**
 * Load and display passkeys list in account modal
 */
export async function loadPasskeysList(): Promise<void> {
  const listContainer = document.getElementById('passkeys-list');
  const messageDiv = document.getElementById('passkey-message');
  const addBtn = document.getElementById('add-passkey-btn');

  if (!listContainer) return;

  // Show loading state
  listContainer.innerHTML = '<div class="passkey-loading">Loading...</div>';

  // Check if passkeys are supported
  if (!isPasskeySupported()) {
    listContainer.innerHTML = `
      <div class="passkey-unsupported">
        ${window.i18n?.t('auth.passkeys.notSupported') || 'Passkeys are not supported in this browser.'}
      </div>
    `;
    if (addBtn) addBtn.style.display = 'none';
    return;
  }

  const passkeys = await listPasskeys();

  if (passkeys.length === 0) {
    listContainer.innerHTML = `
      <div class="passkey-empty">
        ${window.i18n?.t('auth.passkeys.noPasskeys') || 'No passkeys registered yet.'}
      </div>
    `;
    return;
  }

  // Render passkeys list
  listContainer.innerHTML = passkeys.map(pk => {
    const createdDate = new Date(pk.createdAt).toLocaleDateString();
    const lastUsed = pk.lastUsedAt
      ? new Date(pk.lastUsedAt).toLocaleDateString()
      : (window.i18n?.t('auth.passkeys.never') || 'Never');

    return `
      <div class="passkey-item" data-id="${pk.id}">
        <div class="passkey-icon">
          <svg viewBox="0 0 24 24" width="24" height="24">
            <path fill="currentColor" d="M12.65 10A5.99 5.99 0 0 0 7 6c-3.31 0-6 2.69-6 6s2.69 6 6 6a5.99 5.99 0 0 0 5.65-4H17v4h4v-4h2v-4H12.65zM7 14c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z"/>
          </svg>
        </div>
        <div class="passkey-info">
          <div class="passkey-name">${escapeHtml(pk.name || 'Passkey')}</div>
          <div class="passkey-meta">
            ${window.i18n?.t('auth.passkeys.created') || 'Created'}: ${createdDate} ·
            ${window.i18n?.t('auth.passkeys.lastUsed') || 'Last used'}: ${lastUsed}
          </div>
        </div>
        <div class="passkey-actions">
          <button type="button" class="btn-icon" onclick="handleRenamePasskey('${pk.id}')" title="${window.i18n?.t('common.rename') || 'Rename'}">
            <svg viewBox="0 0 24 24" width="16" height="16">
              <path fill="currentColor" d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
            </svg>
          </button>
          <button type="button" class="btn-icon btn-icon-danger" onclick="handleDeletePasskey('${pk.id}')" title="${window.i18n?.t('common.delete') || 'Delete'}">
            <svg viewBox="0 0 24 24" width="16" height="16">
              <path fill="currentColor" d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
            </svg>
          </button>
        </div>
      </div>
    `;
  }).join('');
}

/**
 * Handle adding a new passkey from account settings
 */
export async function handleAddPasskey(): Promise<void> {
  const messageDiv = document.getElementById('passkey-message');
  const addBtn = document.getElementById('add-passkey-btn') as HTMLButtonElement;

  if (messageDiv) {
    messageDiv.textContent = '';
    messageDiv.className = 'passkey-message';
  }

  if (addBtn) {
    addBtn.disabled = true;
  }

  const result = await registerPasskey();

  if (addBtn) {
    addBtn.disabled = false;
  }

  if (result.success) {
    if (messageDiv) {
      messageDiv.textContent = window.i18n?.t('auth.passkeys.addSuccess') || 'Passkey added successfully!';
      messageDiv.className = 'passkey-message passkey-message-success';
    }
    // Reload the list
    await loadPasskeysList();
    // Clear success message after 3 seconds
    setTimeout(() => {
      if (messageDiv) {
        messageDiv.textContent = '';
        messageDiv.className = 'passkey-message';
      }
    }, 3000);
  } else {
    if (messageDiv) {
      messageDiv.textContent = result.error || (window.i18n?.t('auth.passkeys.addError') || 'Failed to add passkey');
      messageDiv.className = 'passkey-message passkey-message-error';
    }
  }
}

/**
 * Handle deleting a passkey
 */
export async function handleDeletePasskey(passkeyId: string): Promise<void> {
  const confirmMsg = window.i18n?.t('auth.passkeys.deleteConfirm') || 'Are you sure you want to delete this passkey?';
  if (!confirm(confirmMsg)) return;

  const messageDiv = document.getElementById('passkey-message');

  const success = await deletePasskey(passkeyId);

  if (success) {
    if (messageDiv) {
      messageDiv.textContent = window.i18n?.t('auth.passkeys.deleteSuccess') || 'Passkey deleted';
      messageDiv.className = 'passkey-message passkey-message-success';
    }
    await loadPasskeysList();
    setTimeout(() => {
      if (messageDiv) {
        messageDiv.textContent = '';
        messageDiv.className = 'passkey-message';
      }
    }, 3000);
  } else {
    if (messageDiv) {
      messageDiv.textContent = window.i18n?.t('auth.passkeys.deleteError') || 'Failed to delete passkey';
      messageDiv.className = 'passkey-message passkey-message-error';
    }
  }
}

/**
 * Handle renaming a passkey
 */
export async function handleRenamePasskey(passkeyId: string): Promise<void> {
  const promptMsg = window.i18n?.t('auth.passkeys.renamePrompt') || 'Enter a new name for this passkey:';
  const newName = prompt(promptMsg);

  if (!newName || newName.trim() === '') return;

  const messageDiv = document.getElementById('passkey-message');

  const success = await renamePasskey(passkeyId, newName.trim());

  if (success) {
    await loadPasskeysList();
  } else {
    if (messageDiv) {
      messageDiv.textContent = window.i18n?.t('auth.passkeys.renameError') || 'Failed to rename passkey';
      messageDiv.className = 'passkey-message passkey-message-error';
    }
  }
}

/**
 * Helper to escape HTML
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

// Make functions available globally
(window as any).handlePasskeyLogin = handlePasskeyLogin;
(window as any).handlePasskeySignup = handlePasskeySignup;
(window as any).signupWithPasskey = signupWithPasskey;
(window as any).registerPasskey = registerPasskey;
(window as any).listPasskeys = listPasskeys;
(window as any).deletePasskey = deletePasskey;
(window as any).renamePasskey = renamePasskey;
(window as any).isPasskeySupported = isPasskeySupported;
(window as any).handleAddPasskey = handleAddPasskey;
(window as any).handleDeletePasskey = handleDeletePasskey;
(window as any).handleRenamePasskey = handleRenamePasskey;
(window as any).loadPasskeysList = loadPasskeysList;
